<script setup>
import { onMounted, reactive } from "vue";

const props = defineProps({
    categories: {
        type: Array,
        required: true,
    },
    canManage: {
        type: Boolean,
        required: true,
    },
});

const teamIdFromUrl = new URLSearchParams(window.location.search).get("teamId");

const data = reactive({
    nestedCategories: [],
    isHeaderCollapse: false,
});

const isShowingAll = () => {
    const qs = new URLSearchParams(window.location.search);
    return !qs.get("category") || qs.get("category") === "0";
};

function toggleCollapse() {
    data.isHeaderCollapse = !data.isHeaderCollapse;
    sessionStorage.setItem(`forum-category-sidebar`, data.isHeaderCollapse);
}

onMounted(() => {
    const categories = props.categories;
    const topLevel = categories.filter(
        (category) => category.parent_category_id === null
    );
    const qs = new URLSearchParams(window.location.search);
    data.nestedCategories = topLevel.map((category) => {
        category.children = categories.filter(
            (c) => c.parent_category_id === category.id
        );

        for (const child of category.children) {
            if (child.id === parseInt(qs.get("category"))) {
                child.isActive = true;
                category.isOpen = true;
                break;
            }
        }

        if (category.id === parseInt(qs.get("category"))) {
            category.isActive = true;
        }

        return category;
    });

    const isHeaderCollapse = sessionStorage.getItem(`forum-category-sidebar`);
    if (isHeaderCollapse) {
        data.isHeaderCollapse = JSON.parse(isHeaderCollapse);
    }
});

const propagateToForm = (category) => {
    const input = document.getElementById("category_query");
    input.value = category?.id ?? 0;
    input.form.submit();
};
</script>

<template>
    <div
        class="md:max-w-[240px] flex-grow-0 flex-shrink-0 w-full md:w-[240px]"
        :class="{ 'md:w-[45px]': data.isHeaderCollapse }"
    >
        <div class="w-full bg-white rounded-lg p-4">
            <div class="flex justify-between">
                <h4
                    class="text-sm font-semibold ml-2"
                    :class="{ 'md:hidden': data.isHeaderCollapse }"
                >
                    Categories
                </h4>
                <button @click="toggleCollapse" class="md:hidden">
                    <ion-icon
                        v-if="!data.isHeaderCollapse"
                        name="chevron-down-outline"
                    ></ion-icon>
                    <ion-icon v-else name="chevron-up-outline"></ion-icon>
                </button>
                <button @click="toggleCollapse" class="hidden md:block">
                    <ion-icon
                        v-if="!data.isHeaderCollapse"
                        name="chevron-back-outline"
                    ></ion-icon>
                    <ion-icon v-else name="chevron-forward-outline"></ion-icon>
                </button>
            </div>
            <ul class="my-2 text-sm" :class="{ hidden: data.isHeaderCollapse }">
                <li class="text-[#687B8D]">
                    <div
                        class="
                            flex
                            items-center
                            gap-2
                            rounded-md
                            hover:bg-[#E6F3F9]
                            p-2
                        "
                        :class="{
                            'bg-[#DDEEF6]': isShowingAll(),
                        }"
                    >
                        <div v-if="canManage" class="w-[30px] h-[30px]"></div>
                        <button
                            class="flex-grow flex-shrink text-left"
                            @click="propagateToForm(null)"
                        >
                            All Category
                        </button>
                    </div>
                </li>
                <li
                    v-for="category in data.nestedCategories"
                    class="text-[#687B8D]"
                    :key="category.id"
                >
                    <div
                        class="
                            flex
                            items-center
                            gap-2
                            rounded-md
                            hover:bg-[#E6F3F9]
                            p-2
                        "
                        :class="{
                            'bg-[#DDEEF6]': category.isActive,
                        }"
                    >
                        <div v-if="canManage && !category.is_predefined">
                            <NewContextMenu>
                                <Modal title="Update Forum Category">
                                    <div class="flex justify-center">
                                        <button
                                            class="
                                                block
                                                w-full
                                                text-left text-gray-500
                                                py-2
                                                px-4
                                                hover:bg-gray-100
                                                rounded-md
                                            "
                                        >
                                            Edit
                                        </button>
                                    </div>

                                    <template #body="{ close }">
                                        <hr class="gray-500 my-4" />
                                        <form
                                            action="/new-forum/category/update"
                                            method="post"
                                        >
                                            <input
                                                type="hidden"
                                                name="id"
                                                :value="category.id"
                                            />
                                            <input
                                                type="hidden"
                                                name="team_id"
                                                :value="teamIdFromUrl"
                                            />
                                            <input
                                                type="hidden"
                                                name="_token"
                                                :value="$root.csrfToken"
                                            />
                                            <div class="my-2">
                                                <label
                                                    for="title"
                                                    class="
                                                        block
                                                        font-semibold
                                                        my-2
                                                        text-sm
                                                    "
                                                >
                                                    Title
                                                </label>
                                                <new-input
                                                    required
                                                    maxlength="128"
                                                    label="Title"
                                                    name="title"
                                                    placeholder="Enter category title"
                                                    :value="category.title"
                                                ></new-input>
                                            </div>

                                            <div
                                                class="
                                                    mt-8
                                                    flex
                                                    items-center
                                                    gap-4
                                                    justify-end
                                                "
                                            >
                                                <app-button
                                                    @click="close"
                                                    type="submit"
                                                    class="
                                                        border border-accent
                                                        text-accent
                                                        !bg-white
                                                    "
                                                >
                                                    Cancel
                                                </app-button>
                                                <app-button
                                                    type="submit"
                                                    class="
                                                        bg-primary
                                                        text-white
                                                    "
                                                    >Update</app-button
                                                >
                                            </div>
                                        </form>
                                    </template>
                                </Modal>

                                <form
                                    action="/new-forum/category/delete"
                                    method="post"
                                >
                                    <input
                                        type="hidden"
                                        name="id"
                                        :value="category.id"
                                    />
                                    <input
                                        type="hidden"
                                        name="team_id"
                                        :value="teamIdFromUrl"
                                    />
                                    <input
                                        type="hidden"
                                        name="_token"
                                        :value="$root.csrfToken"
                                    />
                                    <button
                                        class="
                                            block
                                            w-full
                                            text-left text-red-500
                                            py-2
                                            px-4
                                            hover:bg-gray-100
                                            rounded-md
                                        "
                                    >
                                        Delete
                                    </button>
                                </form>
                            </NewContextMenu>
                        </div>
                        <div
                            v-else-if="canManage"
                            class="w-[30px] h-[30px]"
                        ></div>
                        <button
                            class="flex-grow flex-shrink text-left"
                            @click="propagateToForm(category)"
                        >
                            {{ category.title }}
                        </button>
                        <button
                            class="
                                flex
                                items-center
                                justify-center
                                p-1
                                hover:bg-[#DDEEF6]
                                rounded-md
                            "
                            v-if="category.children.length > 0"
                            @click="category.isOpen = !category.isOpen"
                        >
                            <ion-icon
                                v-if="!category.isOpen"
                                name="chevron-down-outline"
                            ></ion-icon>
                            <ion-icon
                                v-else
                                name="chevron-up-outline"
                            ></ion-icon>
                        </button>
                    </div>
                    <ul v-if="category.isOpen" class="pl-4">
                        <li
                            :key="subCategory.id"
                            v-for="subCategory in category.children"
                            @click="propagateToForm(subCategory)"
                            class="
                                px-4
                                py-2
                                border-l border-l-[#BCC7D1]
                                hover:bg-[#E6F3F9]
                                rounded-r-md
                                cursor-pointer
                            "
                            :class="{
                                'bg-[#DDEEF6]': subCategory.isActive,
                            }"
                        >
                            {{ subCategory.title }}
                        </li>
                    </ul>
                </li>
            </ul>

            <Modal title="Create Forum Category" v-if="canManage">
                <div class="flex justify-center">
                    <button
                        class="
                            flex
                            items-center
                            gap-2
                            bg-gradient-to-br
                            from-primary
                            to-primaryLight
                            text-white text-[14px]
                            rounded-lg
                            p-1
                            px-4
                            mt-4
                        "
                        :style="`${
                            data.isHeaderCollapse && 'padding: 2px!important;'
                        }`"
                        v-if="canManage"
                    >
                        <ion-icon name="add-outline"></ion-icon>
                        <span v-if="!data.isHeaderCollapse"
                            >Create Category</span
                        >
                    </button>
                </div>

                <template #body="{ close }">
                    <hr class="gray-500 my-4" />
                    <form action="/new-forum/category/create" method="post">
                        <input
                            type="hidden"
                            name="team_id"
                            :value="teamIdFromUrl"
                        />
                        <input
                            type="hidden"
                            name="_token"
                            :value="$root.csrfToken"
                        />
                        <div class="my-2">
                            <label
                                for="title"
                                class="block font-semibold my-2 text-sm"
                            >
                                Title
                            </label>
                            <new-input
                                required
                                maxlength="128"
                                label="Title"
                                name="title"
                                placeholder="Enter category title"
                            ></new-input>
                        </div>

                        <div class="mt-8 flex items-center gap-4 justify-end">
                            <app-button
                                @click="close"
                                type="submit"
                                class="
                                    border border-accent
                                    text-accent
                                    !bg-white
                                "
                            >
                                Cancel
                            </app-button>
                            <app-button
                                type="submit"
                                class="bg-primary text-white"
                                >Submit</app-button
                            >
                        </div>
                    </form>
                </template>
            </Modal>
        </div>
    </div>
</template>

<style scoped></style>
